import { useEffect, useState } from "react";
import { QrCode, Store, Loader2, Sparkles } from "lucide-react";
import { http, rupiah } from "@/lib/api";
import ThemeScope from "@/components/ThemeScope";
import { PRESETS } from "@/lib/themes";


function SocialLinks({ links }) {
  const items = [
    [
      "facebook",
      "Facebook",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M13.5 22v-8h2.7l.4-3h-3.1V9.1c0-.9.2-1.5 1.5-1.5h1.7V4.9c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3V11H7.2v3H10v8h3.5Z" />
      </svg>,
    ],
    [
      "instagram",
      "Instagram",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>,
    ],
    [
      "whatsapp",
      "WhatsApp",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M12 2a9.7 9.7 0 0 0-8.4 14.6L2 22l5.6-1.5A9.8 9.8 0 1 0 12 2Zm0 17.6c-1.5 0-3-.4-4.2-1.2l-.3-.2-3.3.9.9-3.2-.2-.3A7.7 7.7 0 1 1 12 19.6Zm4.2-5.8c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1-1.5-.7-2.5-1.3-3.5-3-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.5l-.7-1.7c-.2-.5-.4-.4-.6-.4H8c-.2 0-.5.1-.7.3-.8.8-1.2 1.9-1.2 3 0 1.8 1.3 3.6 1.5 3.8.2.2 2.6 4 6.4 5.5.9.4 1.6.6 2.1.7.9.3 1.7.2 2.4.1.7-.1 2.3-.9 2.6-1.8.3-.9.3-1.6.2-1.8-.1-.1-.3-.2-.5-.3Z" />
      </svg>,
    ],
    [
      "google_maps",
      "Google Maps",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
      </svg>,
    ],
  ];

  const visible = items.filter(([key]) => {
    const value = links?.[key];
    return typeof value === "string" && value.trim();
  });

  if (!visible.length) return null;

  return (
    <div className="mt-3 flex items-center justify-center gap-2.5">
      {visible.map(([key, label, icon]) => (
        <a
          key={key}
          href={links[key].trim()}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-muted-foreground transition hover:border-primary hover:text-primary active:scale-95"
        >
          {icon}
        </a>
      ))}
    </div>
  );
}

export default function Landing() {
  const [data, setData] = useState(null);
  const [tokens, setTokens] = useState(PRESETS.midnight_noodle.tokens);

  useEffect(() => {
    http.get("/menu/preview").then(({ data }) => {
      setData(data);
      setTokens(data.store?.theme?.tokens || PRESETS.midnight_noodle.tokens);
    }).catch(() => setData({ store: { name: "Nama Kedai", tagline: "Mau makan apa hari ini?", hero_image: "", footer_text: "QR Table Ordering" }, categories: [] }));
  }, []);

  if (!data) return <div className="flex min-h-dvh items-center justify-center bg-neutral-950"><Loader2 className="wm-spin text-neutral-500" /></div>;

  return (
    <ThemeScope tokens={tokens} className="min-h-dvh">
      <div className="mx-auto max-w-5xl px-5 pb-20">
        {/* Hero */}
        <div className="relative mt-5 overflow-hidden rounded-[2rem] border border-border">
          <img src={data.store.hero_image || "https://images.unsplash.com/photo-1526318896980-cf78c088247c?q=85&w=1400"} alt="hero" className="h-72 w-full object-cover md:h-96" style={{ objectPosition: data.store.hero_position || "50% 50%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-black/30" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
            <div className="mb-3 flex items-center gap-3">
              <div className={`grid h-12 w-12 place-items-center overflow-hidden bg-card ${data.store.logo_shape === "circle" ? "rounded-full" : "rounded-2xl"}`}>
                {data.store.logo_url ? <img src={data.store.logo_url} alt="logo" className="h-full w-full object-cover" /> : <Store size={22} className="text-primary" />}
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[11px] font-black uppercase tracking-wide text-primary-foreground"><Sparkles size={12} /> Pratinjau Menu</span>
            </div>
            <h1 className="font-heading text-4xl font-extrabold leading-[0.95] tracking-tight text-white md:text-6xl">{data.store.name}</h1>
            <p className="mt-3 max-w-lg text-sm text-white/80 md:text-base">{data.store.tagline}</p>
          </div>
        </div>

        {/* Scan hint */}
        <div className="mt-5 flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <div className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-primary/15 text-primary"><QrCode size={20} /></div>
          <p className="text-sm text-muted-foreground"><b className="text-card-foreground">Mau pesan?</b> Scan QR yang ada di meja kamu untuk memesan langsung dari HP. Halaman ini hanya pratinjau menu & harga.</p>
        </div>

        {/* Menu */}
        {data.categories.map((c, ci) => (
          <section key={c.id} className="mt-10">
            <h2 className="mb-4 font-heading text-2xl font-extrabold tracking-tight md:text-3xl">{c.name}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {c.products.map((p, i) => (
                <article key={p.id} className="wm-rise group overflow-hidden rounded-3xl border border-border bg-card" style={{ animationDelay: `${(ci * 3 + i) * 40}ms` }}>
                  <div className="relative h-44 overflow-hidden bg-muted">
                    {p.image_url ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="grid h-full place-items-center text-muted-foreground"><Store size={26} /></div>}
                    {p.featured && <span className="absolute left-3 top-3 rounded-full bg-primary px-2.5 py-1 text-[10px] font-black text-primary-foreground">★ FAVORIT</span>}
                    {!p.available && <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur">Habis</span>}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-bold leading-tight text-card-foreground">{p.name}</h3>
                    <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">{p.description}</p>
                    <div className="mt-3 font-heading text-xl font-extrabold text-primary">{rupiah(p.price)}</div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-12 text-center text-xs text-muted-foreground">
          <div>
            © {new Date().getFullYear()} {data.store.name} — {data.store.footer_text || "QR Table Ordering"}
          </div>
          <SocialLinks links={data.store.social_links} />
        </div>
      </div>
    </ThemeScope>
  );
}
